import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  useSnackbar
} from '@growdash/design-system/atoms';

// store
import { RootState } from '@store/index';
// hooks
// utils
import { fNumber } from '@utils/format-number';
import { currencyString } from '@utils/helpers';
import { OtherCostResponse, OtherCostVarient } from '@utils/interfaces';
// api
import {
  ConfirmDeleteDialog,
  DataTable,
  MultiSelect
} from '@growdash/design-system/molecules';
import { EyeIcon, MoreHorizontal } from 'lucide-react';
import { ManagementService } from '@modules/management/api';

export default function OtherCostTab() {
  const { BRANDS } = useSelector((state: RootState) => state.filter);
  const currency = currencyString();

  const [selectedBrandIds, setSelectedBrandIds] = useState<
    number[] | undefined
  >(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [tableData, setTableData] = useState<OtherCostResponse | null>(null);
  const [tableLoading, setLoading] = useState<boolean>(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] =
    useState<OtherCostVarient | null>(null);
  const [addCommission, setAddCommission] = useState<OtherCostVarient>({
    id: 0,
    name: '',
    costPercentage: 0,
    costPerOrder: 0,
    costPerMonth: 0
  });
  const [, setDeleteResponse] = useState<any[]>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleAddOtherCostModal = async (id: number) => {
    setAddModalOpen(true);
    setAddCommission({
      ...addCommission,
      id: id
    });
  };
  const tableLabels = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Brand',
      align: 'center'
    },
    {
      id: 'action',
      header: 'Action',
      align: 'center',
      cell: ({ row }: any) => {
        const detail = row.original;
        return (
          <Dialog>
            <DialogTrigger disabled={detail.value?.length === 0}>
              <EyeIcon className='h-[17px] text-[#71717a] cursor-pointer' />
            </DialogTrigger>
            <DialogContent className='min-w-[760px]'>
              <DialogHeader>
                <DialogTitle>Detail Table</DialogTitle>
                <Button onClick={() => handleAddOtherCostModal(detail.id)}>
                  Add
                </Button>
              </DialogHeader>
              <DataTable
                columns={detailTableLabels}
                data={detail.otherCost || []}
                isBorderActive
              />
            </DialogContent>
          </Dialog>
        );
      }
    }
  ];

  const detailTableLabels = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      align: 'center'
    },
    {
      id: 'costPercentage',
      accessorKey: 'costPercentage',
      header: 'Cost %',
      align: 'center',
      cell: ({ row }: any) => (
        <div>{fNumber(row.getValue('costPercentage'))} %</div>
      )
    },
    {
      id: 'costPerOrder',
      accessorKey: 'costPerOrder',
      header: 'Cost Per Order',
      align: 'center',
      cell: ({ row }: any) => (
        <div>
          {fNumber(row.getValue('costPerOrder'))} {currency}
        </div>
      )
    },
    {
      id: 'costPerMonth',
      accessorKey: 'costPerMonth',
      header: 'Cost Per Month',
      align: 'center',
      cell: ({ row }: any) => (
        <div>
          {fNumber(row.getValue('costPerMonth'))} {currency}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'center',
      cell: ({ row }: any) => (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem
              onClick={() => {
                setSelectedCommission(row.original);
                setEditModalOpen(true);
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSelectedCommission(row.original);
                setDeleteModalOpen(true);
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const handleDelete = useCallback(async () => {
    onDeleteOtherCost(Number(selectedCommission?.id));
    // setSelectedRow(null);
    // eslint-disable-next-line no-restricted-globals, react-hooks/exhaustive-deps
  }, [selectedCommission?.id]);

  const fetchOtherCost = useCallback(
    async (pageNumber: number = page + 1, take: number = rowsPerPage) => {
      try {
        setLoading(true);
        const otherCostResponse = await ManagementService?.getAllOtherCost(
          pageNumber,
          take,
          selectedBrandIds
        );
        setTableData(otherCostResponse);
      } catch (error) {
        enqueueSnackbar(error?.message, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, selectedBrandIds, enqueueSnackbar]
  );

  const onDeleteOtherCost = async (id: number) => {
    try {
      const deleteResponse = await ManagementService?.DeleteOtherCost(id);
      setDeleteResponse(deleteResponse);
      fetchOtherCost(page + 1, rowsPerPage);
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      // setLoading(false)
    }
  };

  useEffect(() => {
    fetchOtherCost(page + 1, rowsPerPage);
  }, [page, rowsPerPage, fetchOtherCost]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchOtherCost(newPage + 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchOtherCost(page + 1, newRowsPerPage);
  };
  const handleAddOtherCost = async () => {
    try {
      await ManagementService.AddOtherCost({
        brandId: addCommission?.id,
        name: addCommission?.name,
        costPercentage: addCommission?.costPercentage,
        costPerOrder: addCommission?.costPerOrder,
        costPerMonth: addCommission?.costPerMonth
      });
      enqueueSnackbar('Cost added successfully!', {
        variant: 'success'
      });
      fetchOtherCost(0 + 1, 10);
      setAddModalOpen(false);
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    } finally {
      setEditModalOpen(false);
    }
  };
  return (
    <>
      <ConfirmDeleteDialog
        open={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title='Delete Commission?'
        content='Are you sure you want to delete this commission? This action cannot be undone.'
        action={
          <Button onClick={() => selectedCommission && handleDelete()}>
            Delete
          </Button>
        }
      />
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Other Cost</DialogTitle>
          </DialogHeader>
          <Input
            name='name'
            placeholder='Name'
            onChange={e => {
              setAddCommission({
                ...addCommission,
                name: e.target.value
              });
            }}
          />
          <Input
            name='costPercentage'
            placeholder='Cost Percentage'
            onChange={e => {
              setAddCommission({
                ...addCommission,
                costPercentage: Number(e.target.value)
              });
            }}
          />
          <Input
            name='costPerOrder'
            placeholder='Cost Per Order'
            onChange={e => {
              setAddCommission({
                ...addCommission,
                costPerOrder: Number(e.target.value)
              });
            }}
          />
          <Input
            name='costPerMonth'
            placeholder='Cost Per Month'
            onChange={e => {
              setAddCommission({
                ...addCommission,
                costPerMonth: Number(e.target.value)
              });
            }}
          />
          <Button onClick={handleAddOtherCost}>Save</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={isEditModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Other Cost</DialogTitle>
          </DialogHeader>
          <Input
            name='name'
            placeholder='name'
            value={selectedCommission?.name}
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                name: e.target.value
              });
            }}
          />
          <Input
            name='costPercentage'
            value={selectedCommission?.costPercentage}
            placeholder='Cost Percentage'
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                costPercentage: Number(e.target.value)
              });
            }}
          />
          <Input
            name='costPerOrder'
            placeholder='Cost Per Order'
            value={selectedCommission?.costPerOrder}
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                costPerOrder: Number(e.target.value)
              });
            }}
          />
          <Input
            name='costPerMonth'
            placeholder='Cost Per Month'
            value={selectedCommission?.costPerMonth}
            onChange={e => {
              if (!selectedCommission) return;
              setSelectedCommission({
                ...selectedCommission,
                costPerMonth: Number(e.target.value)
              });
            }}
          />
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                if (!selectedCommission) return;
                await ManagementService.UpdateOtherCost(
                  {
                    brandId: selectedCommission.id,
                    name: selectedCommission.name,
                    costPercentage: selectedCommission.costPercentage,
                    costPerOrder: selectedCommission.costPerOrder,
                    costPerMonth: selectedCommission.costPerMonth
                  },
                  selectedCommission.id
                );
                enqueueSnackbar('Cost updated successfully!', {
                  variant: 'success'
                });
                fetchOtherCost(0 + 1, 10);
              } catch (error) {
                enqueueSnackbar(error?.message, { variant: 'error' });
              } finally {
                setEditModalOpen(false);
              }
            }}
          >
            Save
          </Button>
        </DialogContent>
      </Dialog>
      <div className='mt-6'>
        <Card>
          <CardHeader>
            <MultiSelect
              placeholder='Select Brands'
              popoverAlign='end'
              onReset={() => setSelectedBrandIds([])}
              className='w-max ml-auto'
              maxCount={2}
              defaultValue={selectedBrandIds ?? ([] as any)}
              options={BRANDS?.map((brand: any) => ({
                label: brand.name,
                value: brand.id
              }))}
              onValueChange={setSelectedBrandIds}
            />
          </CardHeader>
          <CardContent>
            <DataTable
              isLoading={tableLoading}
              columns={tableLabels}
              data={tableData?.data || []}
              isBorderActive={false}
              isPaginationActive
              isServerSide
              pagination={{
                pageSize: rowsPerPage,
                pageIndex: page,
                pageCount: Math.ceil((tableData?.total ?? 0) / rowsPerPage),
                onPageChange: handlePageChange,
                onRowsPerPageChange: handleRowsPerPageChange
              }}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
